/**
 * Stream registry for provider progress events.
 *
 * Provides configuration for N event streams in the dashboard,
 * allowing each agent/source to have its own stream with
 * configurable metadata (label, color, order).
 */

/** Accent color options for event stream panes */
export type AccentColor =
  | "indigo"
  | "violet"
  | "emerald"
  | "amber"
  | "cyan"
  | "rose"
  | "gray";

/** Configuration for an event stream */
export interface EventStreamConfig {
  /** Unique identifier for the stream */
  id: string;
  /** Display label for the stream pane header */
  label: string;
  /** Accent color for visual distinction */
  accentColor: AccentColor;
  /** Sort order (lower numbers appear first) */
  order: number;
}

/**
 * Registry of known event streams.
 *
 * Add new streams here as they are introduced.
 * The dashboard will automatically render panes for streams
 * that have events, using these configurations.
 */
export const STREAM_REGISTRY: Record<string, EventStreamConfig> = {
  embedding: {
    accentColor: "cyan",
    id: "embedding",
    label: "Embedding",
    order: -2,
  },
  explore: {
    accentColor: "emerald",
    id: "explore",
    label: "Pre-Explore",
    order: -1,
  },
  main: {
    accentColor: "indigo",
    id: "main",
    label: "Implementation",
    order: 0,
  },
  repo_indexing: {
    accentColor: "cyan",
    id: "repo_indexing",
    label: "Repo Indexing",
    order: 0,
  },
  reviewer: {
    accentColor: "violet",
    id: "reviewer",
    label: "Reviewer",
    order: 1,
  },
  ui_reviewer: {
    accentColor: "rose",
    id: "ui_reviewer",
    label: "UI Review",
    order: 2,
  },
};

/**
 * Default stream configuration for unknown stream IDs.
 */
const DEFAULT_STREAM_CONFIG: Omit<EventStreamConfig, "id"> = {
  accentColor: "gray",
  label: "Agent",
  order: 999,
};

/**
 * Get the configuration for a stream by ID.
 *
 * Returns the registered config if the stream ID is known,
 * otherwise returns a default configuration with the stream ID as label.
 */
export function getStreamConfig(streamId: string): EventStreamConfig {
  const registered = STREAM_REGISTRY[streamId] as EventStreamConfig | undefined;
  if (registered) {
    return registered;
  }

  // Return a default config for unknown streams
  return {
    accentColor: DEFAULT_STREAM_CONFIG.accentColor,
    id: streamId,
    label: streamId.charAt(0).toUpperCase() + streamId.slice(1),
    order: DEFAULT_STREAM_CONFIG.order,
  };
}
